import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { stagePhaseHint, isGroupLeague, stagePhaseOf, priorityForPhaseChange } from '../utilities/stagePriority';
import StagePhasePicker from './StagePhasePicker';
import '../styles/createMatchModal.css';

function EditStageModal({ isOpen, onClose, onSuccess, stage, league, season, stages = [], format = null }) {
  const dispatch = useDispatch();
  
  const [stageName, setStageName] = useState('');
  const [stageShortName, setStageShortName] = useState('');
  const [priority, setPriority] = useState('');
  const [phase, setPhase] = useState('group');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (stage) {
      setStageName(stage.name || '');
      setStageShortName(stage.short_name || '');
      // Стадия без номера в лиге с группами: подставляем номер её этапа,
      // иначе правку (даже названия) нельзя было сохранить
      setPriority(stage.priority
        || (isGroupLeague(format) ? priorityForPhaseChange(stages, format, stage, stagePhaseOf(stage, format)) : ''));
      setPhase(stagePhaseOf(stage, format));
    }
  }, [stage, format, stages]);

  // В лиге с группами этап выбирают явно: смена этапа пересчитывает номер
  const changePhase = (next) => {
    setPhase(next);
    if (!stage) return;
    setPriority(next === stagePhaseOf(stage, format)
      ? (stage.priority || '')
      : priorityForPhaseChange(stages, format, stage, next));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stageName.trim()) {
      setMessage({ text: 'Введите название стадии', type: 'error' });
      return;
    }
    const priorityNumber = parseInt(priority, 10);
    if (!/^\d+$/.test(String(priority).trim()) || priorityNumber < 1) {
      setMessage({ text: 'Порядковый номер — целое число от 1', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const stageData = {
        name: stageName.trim(),
        short_name: stageShortName.trim() || null,
        priority: priorityNumber,
        group_id: stage.group_id || null,
        league_id: parseInt(league.value, 10),
        season_id: parseInt(season.value, 10),
        stages_amount: 1,
        matches: []
      };

      const response = await axios.post(
        BASE_URL.url + `/leagues/changeStage/${stage.id}`,
        stageData,
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      setMessage({ text: 'Стадия успешно обновлена!', type: 'success' });
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating stage:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Ошибка при обновлении стадии', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !stage) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Редактирование стадии</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section-modal">
              <label className="form-label-modal form-label-required">
                Название стадии
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                placeholder="Например: 1 Тур, Полуфинал, Группа А"
                maxLength={100}
              />
            </div>

            <div className="form-section-modal">
              <label className="form-label-modal">
                Короткое название (опционально)
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageShortName}
                onChange={(e) => setStageShortName(e.target.value)}
                placeholder="Например: 1Т, ПФ"
                maxLength={50}
              />
            </div>

            {isGroupLeague(format) ? (
              <>
                <StagePhasePicker format={format} phase={phase} onChange={changePhase} disabled={loading} />
                <div className="form-section-modal">
                  <small style={{ color: '#718096', fontSize: '13px' }}>
                    Номер стадии: {priority || '—'}. {phase === 'group' ? 'Матчи идут в таблицу групп.' : 'Стадия в плей-офф.'}
                  </small>
                </div>
              </>
            ) : (
              <div className="form-section-modal">
                <label className="form-label-modal">
                  Порядковый номер (priority)
                </label>
                <input
                  className="input-field-modal"
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  placeholder="Порядковый номер"
                  min="1"
                />
                <small style={{ color: '#718096', fontSize: '13px', marginTop: '4px' }}>
                  {stagePhaseHint(parseInt(priority, 10), format) || 'Номер стадии по порядку: 1 тур — 1, 2 тур — 2 и т.д.'}
                </small>
              </div>
            )}

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading}
                className="btn-modal btn-primary-modal"
              >
                {loading ? 'Сохранение...' : 'Сохранить изменения'}
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditStageModal;
